import { useEffect, useState } from 'react';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// components
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import Link from '@/components/utilities/SmartLink';
import { Icon } from '@/components/ui/icon';
import { Slider } from '@/components/ui/slider';

// utilities
import { getImagePath } from '@/utils/images';
import { formatCurrency } from '@/utils/formatting';

const YEAR = 'year';

const PricingBlock = ({ defaultType, types, pricing, pricingContent: content, currency, savingsLabel }) => {
  // Use props directly as initial state, avoiding unnecessary effect
  const initialPeriod = defaultType || types?.[0]?.type || YEAR;
  const [period, setPeriod] = useState(initialPeriod);
  const [exchangeRate, setExchangeRate] = useState(null);

  const needsConversion = currency && currency !== 'CZK';

  useEffect(() => {
    if (!needsConversion) return;
    fetch(`/api/exchange-rate?currency=${currency}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setExchangeRate(data))
      .catch(() => setExchangeRate(null));
  }, [needsConversion, currency]);

  const effectiveCurrency = needsConversion && exchangeRate ? currency : 'CZK';
  const convertPrice = (priceCzk) => {
    if (!needsConversion || !exchangeRate || priceCzk == null) return priceCzk;
    return priceCzk / (exchangeRate.rate / exchangeRate.amount);
  };

  return (
    <div className="mx-auto my-8 w-[90vw] lg:mt-16 xl:w-(--container-width)">
      <VisibleLoading delay={150}>
        {({ ref, className }) => (
          <div ref={ref} className={`relative mx-auto mb-6 flex w-fit flex-col items-center ${className || ''}`.trim()}>
            <div className="bg-brand-100 flex w-fit gap-1 rounded-full p-1 font-medium text-black">
              {(types || []).map((type) => (
                <div
                  key={type.id}
                  onClick={() => setPeriod(type.type)}
                  className={`ease cursor-pointer rounded-full px-3.5 py-1.5 text-center transition-colors duration-300 ${period === type.type ? 'bg-brand-400 font-semibold text-white' : 'text-black'}`}
                >
                  {type.label}
                </div>
              ))}
            </div>
            <SavingsBadge label={savingsLabel} active={period === YEAR} />
          </div>
        )}
      </VisibleLoading>

      <div className="flex flex-wrap items-stretch justify-center gap-y-6">
        {(content || []).map((card, index) => (
          <VisibleLoading key={card.id} delay={(index + 1) * 100}>
            {({ ref, className }) => (
              <PricingCard
                innerRef={ref}
                className={className}
                card={card}
                pricing={pricing}
                period={period}
                currency={effectiveCurrency}
                convertPrice={convertPrice}
              />
            )}
          </VisibleLoading>
        ))}
      </div>
    </div>
  );
};
export default PricingBlock;

const SavingsBadge = ({ label, active }) => {
  if (!label) return null;

  return (
    <div
      className={`mt-3 flex w-fit items-center gap-2 rounded-full py-1.5 pr-3 pl-1.5 text-sm leading-5 font-medium whitespace-nowrap transition-colors duration-300 sm:absolute sm:top-1/2 sm:left-full sm:mt-0 sm:ml-3 sm:-translate-y-1/2 ${active ? 'bg-[#CFFCE2] text-[#025237]' : 'bg-[#E7E7E7] text-white'}`}
    >
      {active ? (
        <svg
          className="shrink-0"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="24" height="24" rx="12" fill="white" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.098 7.39016L9.93797 14.3002L8.03797 12.2702C7.68797 11.9402 7.13797 11.9202 6.73797 12.2002C6.34797 12.4902 6.23797 13.0002 6.47797 13.4102L8.72797 17.0702C8.94797 17.4102 9.32797 17.6202 9.75797 17.6202C10.168 17.6202 10.558 17.4102 10.778 17.0702C11.138 16.6002 18.008 8.41016 18.008 8.41016C18.908 7.49016 17.818 6.68016 17.098 7.38016V7.39016Z"
            fill="#12B76A"
          />
        </svg>
      ) : (
        <svg
          className="shrink-0"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="24" height="24" rx="12" fill="white" />
          <path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" stroke="#888888" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      {label}
    </div>
  );
};

const PricingCard = ({ innerRef, className, card, period, pricing, currency, convertPrice }) => {
  return (
    <div ref={innerRef} className={`flex xl:basis-1/3 xl:px-3 ${className || ''}`.trim()}>
      <div
        className={`flex flex-col overflow-hidden rounded-2xl border-2 text-center shadow-2xl/15 ${card.popular ? 'border-green-500' : 'border-transparent'}`}
      >
        <div
          className={`txt-lg flex h-10 items-center justify-center font-medium text-white ${card.popular ? 'bg-green-500' : 'bg-transparent'}`}
        >
          {card.popular ? (
            <span className="flex items-center gap-2">
              <Icon type="stars" width={16} height={16} />
              {card.popularLabel}
            </span>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col px-10 pt-6">
          <div className="mb-6 h-12 text-center">
            {card?.icon && (
              <Image
                src={getImagePath(card.icon)}
                width={card.icon.width}
                height={card.icon.height}
                alt={card.title}
                sizes="48px"
                className="mx-auto h-12 w-12 object-contain"
              />
            )}
          </div>
          <h2>{card.title}</h2>
          <div className="txt-md text-brand-400 mb-4 font-medium">{card.subtitle}</div>
          <div className="txt-lg mb-8 min-h-[60px] font-medium">{card.text}</div>

          <div className="mb-6">
            {(card.pricingCardContent || []).map((component) =>
              component.__typename === 'ComponentComponentsH5' ? (
                <div key={component.id} className="heading-5">
                  {component.text}
                </div>
              ) : component.__typename === 'ComponentComponentsPricingSlider' ? (
                <PricingSlider
                  key={component.id}
                  label={component.label}
                  period={period}
                  pricing={pricing}
                  currency={currency}
                  convertPrice={convertPrice}
                  yearlyNote={component.yearlyNote}
                  priceSuffix={component.priceSuffix}
                />
              ) : null
            )}
          </div>

          <div className="mb-8">
            {card.button && (
              <Button
                variant={card.button.variant}
                size={card.button.size}
                asChild={true}
                className={`basis-full xl:basis-auto`}
              >
                <Link href={card.button.url || ''}>
                  <Icon type={card.button.icon} /> {card.button.label}
                </Link>
              </Button>
            )}
          </div>

          <div className="flex min-h-[190px] flex-1 flex-col gap-4 pb-4">
            {card.features.map((feature, index) => (
              <div key={index} className="text-grey-950 flex items-center gap-3 text-left">
                {feature.checked ? (
                  <svg
                    className="min-w-6"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" rx="12" fill="#D1FADF" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.098 7.39016L9.93797 14.3002L8.03797 12.2702C7.68797 11.9402 7.13797 11.9202 6.73797 12.2002C6.34797 12.4902 6.23797 13.0002 6.47797 13.4102L8.72797 17.0702C8.94797 17.4102 9.32797 17.6202 9.75797 17.6202C10.168 17.6202 10.558 17.4102 10.778 17.0702C11.138 16.6002 18.008 8.41016 18.008 8.41016C18.908 7.49016 17.818 6.68016 17.098 7.38016V7.39016Z"
                      fill="#12B76A"
                    />
                  </svg>
                ) : (
                  <svg
                    className="min-w-6"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" rx="12" fill="#E7E7E6" />
                    <path
                      d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5"
                      stroke="#B0B0B0"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                <span className="txt-lg tracking-tight">{feature.label}</span>
              </div>
            ))}
          </div>

          <div className="txt-sm text-grey-500 flex h-12 items-center justify-center">{card.note}</div>
        </div>
      </div>
    </div>
  );
};

export const PricingSlider = ({ label, period, pricing, currency, convertPrice, yearlyNote, priceSuffix }) => {
  const [index, setIndex] = useState([0]);

  const pricingPlanIndex = index[0] || 0;
  const pricingPlan = pricing?.[pricingPlanIndex] || pricing[0];
  const rawPrice = pricingPlan?.value?.[period];
  const price = convertPrice ? convertPrice(rawPrice) : rawPrice;

  // Yearly total = 10 × undiscounted monthly payment of the selected plan
  const monthlyBeforeDiscount = pricingPlan?.value?.month;
  const yearlyTotalCzk = monthlyBeforeDiscount != null ? monthlyBeforeDiscount * 10 : null;
  const yearlyTotal = convertPrice ? convertPrice(yearlyTotalCzk) : yearlyTotalCzk;
  const yearlyNoteText =
    period === YEAR && yearlyNote && Number.isFinite(yearlyTotal)
      ? yearlyNote.replaceAll('{price}', formatCurrency(yearlyTotal, { currency: currency || 'CZK' }))
      : null;

  return (
    <div>
      <div className="heading-5">
        {price != null ? formatCurrency(price, { currency: currency || 'CZK' }) : '-'}
        {priceSuffix && <span className="text-grey-700 ml-2 text-[16px] font-light">{priceSuffix}</span>}
      </div>
      {yearlyNoteText && <div className="txt-md text-brand-400">{yearlyNoteText}</div>}
      <div className="txt-md text-grey-700 mt-2 mb-6">{`${pricingPlan?.range[0]} - ${pricingPlan?.range[1]} ${label}`}</div>
      <div className="mb-3">
        <Slider min={0} max={pricing.length - 1} value={index} onValueChange={setIndex} step={1} showSteps={true} />
      </div>
      <div className="flex justify-between">
        <span className="txt-sm text-grey-500">{pricing?.[0]?.range?.[0]}</span>
        <span className="txt-sm text-grey-500">{pricing?.[pricing.length - 1]?.range?.[1]}</span>
      </div>
    </div>
  );
};
